import { FC, Children, isValidElement, createElement, PropsWithChildren, ReactElement, ReactNode, useMemo } from 'react';

import logger from '@eneco/common/log';
import { trimTrailingSlashes } from '@eneco/common/path';
import { useLayoutData } from '@sitecore/common';

import { useRouter, useRouterPath, RouterContext } from './useRouter';
import { isMatch } from './util';

export interface RouteProps {
  route: string;
  component: FC;
}

type RouteComponent = ReactElement<RouteProps>;

const normalize = (path: string): string => trimTrailingSlashes(path);

const isRouteComponent = (component: ReactElement): component is ReactElement<RouteProps> => {
  if (component.props && typeof component.props === 'object') {
    return 'route' in component.props;
  }
  return false;
};
const getRouteComponents = (components: ReactNode): RouteComponent[] =>
  Children.toArray(components).filter(isValidElement).filter(isRouteComponent);

export type ErrorPaths = 'notfound' | 'error';
export const errorPaths = { notfound: '404-not-found', error: '500-error' };

export interface RouterProviderProps {
  /**
   * The application's root path, from the caller rather than from Sitecore
   * layout data. Supplying it keeps `useLayoutData` out of the render entirely,
   * which is what lets an application route on a lane that has no Sitecore
   * context: that hook throws when no provider is mounted rather than
   * returning a default.
   *
   * Include the container base path, because `activePath` carries it too and
   * this value is subtracted from it.
   */
  basePath?: string;
  /**
   * Whether the page permits front-end routing. Only an explicit `false` is
   * reported: a caller that says nothing has opted out of the check, whereas
   * Sitecore layout data always answers, reading a missing field as a refusal.
   */
  allowFrontEndRouting?: boolean;
}

/** Derives the active route and publishes both values. Knows nothing about where they came from. */
const RoutingContext: FC<PropsWithChildren<{ routerBasePath: string; allowFrontEndRouting?: boolean }>> = ({
  routerBasePath,
  allowFrontEndRouting,
  children,
}) => {
  const { activePath } = useRouter();

  if (allowFrontEndRouting === false) {
    logger.error('mPPGjA', 'Router is only allowed with "allowFrontEndRouting" enabled', {});
  }

  const activeRoute = activePath ? normalize(activePath).replace(normalize(routerBasePath), '') + '/' : null;

  const contextValue = useMemo(() => ({ routerBasePath, activeRoute }), [routerBasePath, activeRoute]);

  return <RouterContext.Provider value={contextValue}>{children}</RouterContext.Provider>;
};

/** The original source of both values: the current route's own Sitecore fields. */
const SitecoreRoutingContext: FC<PropsWithChildren> = ({ children }) => {
  const {
    context: { basePath = '/' },
    route: { fields = {} },
  } = useLayoutData();

  return (
    <RoutingContext
      routerBasePath={String(fields.frontEndRootPath?.value ?? basePath)}
      allowFrontEndRouting={fields.allowFrontEndRouting?.value === true}>
      {children}
    </RoutingContext>
  );
};

export const RouterProvider: FC<PropsWithChildren<RouterProviderProps>> = ({
  basePath,
  allowFrontEndRouting,
  children,
}) =>
  basePath === undefined ? (
    <SitecoreRoutingContext>{children}</SitecoreRoutingContext>
  ) : (
    <RoutingContext routerBasePath={basePath} allowFrontEndRouting={allowFrontEndRouting}>
      {children}
    </RoutingContext>
  );

export const Routes: FC<PropsWithChildren> = ({ children }) => {
  const { routerBasePath, activeRoute } = useRouterPath();

  if (!activeRoute) return null;

  const routeComponents = getRouteComponents(children);
  const route = routeComponents.find(child => isMatch(child.props.route, activeRoute)) ?? null;

  if (!route) {
    logger.error('O4w0az', `No match for route "${activeRoute}" in "${routerBasePath}"`);
    return null;
  }

  const match = isMatch(route.props.route, activeRoute);
  const params = match ? match.params : {};

  return createElement(route.props.component, params);
};

export const Router: FC<PropsWithChildren> = ({ children }) => (
  <RouterProvider>
    <Routes>{children}</Routes>
  </RouterProvider>
);

export const Route = ({ route, component }: RouteProps) => null;
